import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { COMPANY_TYPE_KEYS, COMPANY_TYPE_LABELS, mediaUrl } from '../../../../shared/courses'

import Btn from '../../../../UI/Btn'

import { STATUS_LABELS } from './labels'

import styles from './style.module.scss'

const FELDER = [
	'title',
	'subtitle',
	'description',
	'status',
	'companyTypes',
	'quizPass',
	'questionsPerQuiz',
	'minWatchPercent',
	'countsForCredit',
	'certificateTitle',
	'certificateText'
]

export const basicsFromCourse = course => ({
	title: course.title || '',
	subtitle: course.subtitle || '',
	description: course.description || '',
	status: course.status || 'DRAFT',
	companyTypes: Array.isArray(course.companyTypes) ? course.companyTypes : [],
	quizPass: course.quizPass ?? 4,
	questionsPerQuiz: course.questionsPerQuiz ?? 5,
	minWatchPercent: course.minWatchPercent ?? 90,
	countsForCredit: course.countsForCredit !== false,
	certificateTitle: course.certificateTitle || '',
	certificateText: course.certificateText || ''
})

/** Prüft die Grunddaten, bevor sie hinausgehen. Liefert einen Satz oder null. */
export const basicsError = (werte, t = text => text) => {
	if (!werte.title.trim()) return t('Please enter a title.')
	const proQuiz = Number(werte.questionsPerQuiz)
	const huerde = Number(werte.quizPass)
	if (!Number.isInteger(proQuiz) || proQuiz < 1 || proQuiz > 20) return t('Questions per quiz: 1 to 20.')
	if (!Number.isInteger(huerde) || huerde < 1 || huerde > proQuiz)
		return t('The pass mark must be between 1 and the number of questions per quiz.')
	const prozent = Number(werte.minWatchPercent)
	if (!Number.isInteger(prozent) || prozent < 50 || prozent > 100) return t('Watched from: 50 to 100 %.')
	return null
}

/**
 * Die Grunddaten eines Kurses: Name, Text, wer ihn sieht, wie streng das Quiz
 * ist und was auf der Urkunde steht.
 */
export default function BasicsTab({ course, onSaved, markDirty }) {
	const { t } = useTranslation()
	const ausgang = useMemo(() => basicsFromCourse(course), [course])
	const [werte, setWerte] = useState(ausgang)
	const [speichert, setSpeichert] = useState(false)
	const [coverLaedt, setCoverLaedt] = useState(false)

	useEffect(() => setWerte(ausgang), [ausgang])

	const geaendert = FELDER.some(feld => JSON.stringify(werte[feld]) !== JSON.stringify(ausgang[feld]))
	useEffect(() => markDirty('basics', geaendert), [geaendert, markDirty])

	const setze = (feld, wert) => setWerte(vorher => ({ ...vorher, [feld]: wert }))

	const umschalten = typ =>
		setze(
			'companyTypes',
			werte.companyTypes.includes(typ) ? werte.companyTypes.filter(x => x !== typ) : [...werte.companyTypes, typ]
		)

	const speichern = event => {
		event.preventDefault()
		const problem = basicsError(werte, t)
		if (problem) return toast.error(problem)

		setSpeichert(true)
		http.put(TApi.ADMIN_COURSE(course.id), {
			...werte,
			title: werte.title.trim(),
			quizPass: Number(werte.quizPass),
			questionsPerQuiz: Number(werte.questionsPerQuiz),
			minWatchPercent: Number(werte.minWatchPercent)
		})
			.then(() => {
				toast.success(t('Saved'))
				return onSaved()
			})
			.finally(() => setSpeichert(false))
		return undefined
	}

	const titelbild = event => {
		const datei = event.target.files?.[0]
		event.target.value = ''
		if (!datei) return
		if (datei.size > 5 * 1024 * 1024) return toast.error(t('The image may be at most 5 MB.'))

		const form = new FormData()
		form.append('file', datei)
		setCoverLaedt(true)
		http.post(TApi.ADMIN_COURSE_COVER(course.id), form)
			.then(() => onSaved())
			.finally(() => setCoverLaedt(false))
		return undefined
	}

	return (
		<form onSubmit={speichern}>
			<div className={styles.card}>
				<h3 className={styles.cardTitle}>{t('Course')}</h3>

				<label className={styles.field}>
					{t('Title')}
					<input type="text" value={werte.title} onChange={e => setze('title', e.target.value)} />
				</label>
				<label className={styles.field}>
					{t('Subtitle')}
					<input type="text" value={werte.subtitle} onChange={e => setze('subtitle', e.target.value)} />
				</label>
				<label className={styles.field}>
					{t('Description')}
					<textarea value={werte.description} onChange={e => setze('description', e.target.value)} />
					<span className={styles.fieldHint}>{t('Shown large on the course page next to the cover image.')}</span>
				</label>

				<h4 className={styles.subTitle}>{t('Cover image')}</h4>
				<div className={styles.cover} style={course.coverUrl ? { backgroundImage: `url("${mediaUrl(course.coverUrl)}")` } : undefined} />
				<label className={styles.upload}>
					<input type="file" accept="image/jpeg,image/png,image/webp" onChange={titelbild} disabled={coverLaedt} />
					<span className={styles.fieldHint}>
						{coverLaedt ? t('Uploading…') : t('JPG, PNG or WebP, at most 5 MB, ideally 16:9 (e.g. 1920 × 1080).')}
					</span>
				</label>
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>{t('Visibility')}</h3>
				<label className={styles.field}>
					{t('Status')}
					<select value={werte.status} onChange={e => setze('status', e.target.value)}>
						{Object.keys(STATUS_LABELS).map(wert => (
							<option key={wert} value={wert}>
								{t(STATUS_LABELS[wert])}
							</option>
						))}
					</select>
					<span className={styles.fieldHint}>
						{t('Draft: only visible here. Published: students see it. Archived: hidden, completions and certificates remain.')}
					</span>
				</label>

				<p className={styles.subTitle}>{t('For which company types?')}</p>
				<div className={styles.checks}>
					{COMPANY_TYPE_KEYS.map(typ => (
						<label key={typ}>
							<input
								type="checkbox"
								checked={werte.companyTypes.includes(typ)}
								onChange={() => umschalten(typ)}
							/>
							{COMPANY_TYPE_LABELS[typ]}
						</label>
					))}
				</div>
				{werte.status === 'PUBLISHED' && werte.companyTypes.length === 0 && (
					<p className={styles.warn}>{t('Published, but for no company type — nobody sees the course.')}</p>
				)}
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>{t('Quiz and completion')}</h3>
				<div className={styles.row}>
					<label className={styles.field}>
						{t('Questions per quiz')}
						<input
							type="number"
							min={1}
							max={20}
							value={werte.questionsPerQuiz}
							onChange={e => setze('questionsPerQuiz', e.target.value)}
						/>
						<span className={styles.fieldHint}>{t('Drawn at random from the question pool of the module.')}</span>
					</label>
					<label className={styles.field}>
						{t('Passed from (correct answers)')}
						<input
							type="number"
							min={1}
							max={20}
							value={werte.quizPass}
							onChange={e => setze('quizPass', e.target.value)}
						/>
					</label>
					<label className={styles.field}>
						{t('Video counts as watched from (%)')}
						<input
							type="number"
							min={50}
							max={100}
							value={werte.minWatchPercent}
							onChange={e => setze('minWatchPercent', e.target.value)}
						/>
						<span className={styles.fieldHint}>{t('Skipping ahead does not count.')}</span>
					</label>
				</div>
				<div className={styles.checks}>
					<label>
						<input
							type="checkbox"
							checked={werte.countsForCredit}
							onChange={e => setze('countsForCredit', e.target.checked)}
						/>
						{t('A completion counts towards the credit on the invoice')}
					</label>
				</div>
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>{t('Certificate')}</h3>
				<label className={styles.field}>
					{t('Title on the certificate')}
					<input
						type="text"
						value={werte.certificateTitle}
						placeholder={werte.title}
						onChange={e => setze('certificateTitle', e.target.value)}
					/>
				</label>
				<label className={styles.field}>
					{t('Text on the certificate')}
					<textarea value={werte.certificateText} onChange={e => setze('certificateText', e.target.value)} />
					<span className={styles.fieldHint}>
						{t('One or two sentences on what the course covered. Name, date, modules and check code are added automatically.')}
					</span>
				</label>
			</div>

			<div className={styles.buttons}>
				<Btn type="submit" attention={geaendert} loading={speichert} disabled={!geaendert || speichert}>
					{t('Save')}
				</Btn>
				{geaendert && (
					<Btn theme="minimal" type="button" onClick={() => setWerte(ausgang)}>
						{t('Undo changes')}
					</Btn>
				)}
			</div>
		</form>
	)
}

BasicsTab.propTypes = {
	course: PropTypes.object.isRequired,
	onSaved: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}
